import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Loading, StatusBadge, fmtMAD, FileUpload, fileSrc } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Sparkles, Loader2, Save, Camera } from "lucide-react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";

const SCORE_KEYS = ["passing", "ball_control", "dribbling", "shooting", "finishing", "defending", "speed", "agility", "strength", "tactical", "decision_making"];
const LABELS = { passing: "Passing", ball_control: "Ball Control", dribbling: "Dribbling", shooting: "Shooting", finishing: "Finishing", defending: "Defending", speed: "Speed", agility: "Agility", strength: "Strength", tactical: "Tactical", decision_making: "Decision Making" };

function Info({ label, value }) {
  return <div><p className="text-xs uppercase font-bold text-muted-foreground">{label}</p><p className="mt-1 font-medium">{value || "—"}</p></div>;
}

export default function PlayerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useApp();
  const [p, setP] = useState(null);
  const [scores, setScores] = useState({});
  const [strengths, setStrengths] = useState("");
  const [weaknesses, setWeaknesses] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const canEval = can(["admin", "coach", "assistant_coach"]);

  const load = () => client.get(`/players/${id}`).then((r) => {
    setP(r.data); setScores(r.data.scores || {}); setStrengths(r.data.strengths || ""); setWeaknesses(r.data.weaknesses || ""); setAnalysis(r.data.ai_analysis || "");
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!p) return <Loading />;

  const saveScores = async () => {
    try { await client.put(`/players/${id}/scores`, { scores, strengths, weaknesses, coach_evaluation: p.coach_evaluation || "" }); toast.success("Evaluation saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runAI = async () => {
    setAiLoading(true);
    try { const r = await client.post("/ai/player-analysis", { player_id: id }); setAnalysis(r.data.analysis); toast.success("Analysis generated"); }
    catch (e) { toast.error(apiError(e)); } finally { setAiLoading(false); }
  };
  const savePhoto = async (f) => {
    try { await client.put(`/players/${id}`, { photo_url: f.url }); toast.success("Photo updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const radarData = SCORE_KEYS.map((k) => ({ subject: LABELS[k], value: Number(scores[k]) || 0 }));

  return (
    <div>
      <Button variant="ghost" onClick={() => nav("/players")} className="mb-4" data-testid="back-btn"><ArrowLeft size={16} className="me-1" /> Back to Players</Button>
      <Card className="p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="relative shrink-0">
            {p.photo_url
              ? <img src={fileSrc(p.photo_url)} alt={p.full_name} className="h-24 w-24 rounded-2xl object-cover" data-testid="player-photo" />
              : <div className="h-24 w-24 rounded-2xl bg-emerald-500 text-white flex items-center justify-center text-4xl font-black">{p.full_name?.[0]?.toUpperCase()}</div>}
            {can(["admin"]) && <div className="absolute -bottom-3 start-1/2 -translate-x-1/2"><FileUpload accept="image/*" size="icon" label={<Camera size={14} />} testid="player-photo-upload" onUploaded={savePhoto} /></div>}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-extrabold font-heading">{p.full_name}</h1>
              <StatusBadge status={p.category} /><StatusBadge status={p.status} />
            </div>
            {p.full_name_ar && <p className="text-muted-foreground" dir="rtl">{p.full_name_ar}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
              <Info label="Player ID" value={p.player_code} />
              <Info label="Age" value={p.age} />
              <Info label="Date of Birth" value={p.dob} />
              <Info label="Position" value={p.position} />
            </div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">Personal & Parent</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">Financial</TabsTrigger>
          <TabsTrigger value="football" data-testid="tab-football">Football & AI</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6"><h3 className="font-bold font-heading mb-4">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <Info label="City" value={p.city} /><Info label="Address" value={p.address} />
                <Info label="Preferred Foot" value={p.preferred_foot} /><Info label="Previous Club" value={p.previous_club} />
                <Info label="Registration Date" value={(p.created_at || "").slice(0, 10)} />
              </div>
            </Card>
            <Card className="p-6"><h3 className="font-bold font-heading mb-4">Parent / Guardian</h3>
              <div className="grid grid-cols-2 gap-4">
                <Info label="Parent Name" value={p.parent_name} /><Info label="Phone" value={p.parent_phone} />
                <Info label="Secondary Phone" value={p.parent_phone2} /><Info label="Email" value={p.parent_email} />
                <Info label="National ID" value={p.parent_national_id} />
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="finance" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <Card className="p-5"><p className="text-xs uppercase font-bold text-muted-foreground">Registration Fee</p><p className="text-xl font-extrabold mt-2">{fmtMAD(p.registration_fee)}</p></Card>
            <Card className="p-5"><p className="text-xs uppercase font-bold text-muted-foreground">Monthly Fee</p><p className="text-xl font-extrabold mt-2">{fmtMAD(p.monthly_fee)}</p></Card>
            <Card className="p-5"><p className="text-xs uppercase font-bold text-muted-foreground">Payments</p><p className="text-xl font-extrabold mt-2">{(p.payments || []).length}</p></Card>
          </div>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border"><tr>{["Receipt", "Amount", "Type", "Month", "Status", "Date"].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase text-muted-foreground">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-border">
                {(p.payments || []).length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No payments recorded</td></tr>}
                {(p.payments || []).map((pay) => (
                  <tr key={pay.id}><td className="px-4 py-3 font-mono text-xs">{pay.receipt_number}</td><td className="px-4 py-3 font-semibold">{fmtMAD(pay.amount)}</td><td className="px-4 py-3 capitalize">{pay.type}</td><td className="px-4 py-3">{pay.month}</td><td className="px-4 py-3"><StatusBadge status={pay.status} /></td><td className="px-4 py-3 text-muted-foreground">{(pay.date || "").slice(0, 10)}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="football" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4"><h3 className="font-bold font-heading">Evaluation Scores</h3>{canEval && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveScores} data-testid="save-scores"><Save size={15} className="me-1" /> Save</Button>}</div>
              <div className="space-y-3 max-h-[360px] overflow-y-auto scrollbar-thin pe-2">
                {SCORE_KEYS.map((k) => (
                  <div key={k}>
                    <div className="flex justify-between text-sm mb-1"><Label>{LABELS[k]}</Label><span className="font-bold text-emerald-600">{scores[k] || 0}</span></div>
                    <Slider value={[Number(scores[k]) || 0]} min={0} max={100} step={1} onValueChange={(v) => setScores({ ...scores, [k]: v[0] })} disabled={!canEval} data-testid={`score-${k}`} />
                  </div>
                ))}
              </div>
            </Card>
            <div className="space-y-6">
              <Card className="p-6">
                <ResponsiveContainer width="100%" height={260}>
                  <RadarChart data={radarData}><PolarGrid /><PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} /><Radar dataKey="value" stroke="#10B981" fill="#10B981" fillOpacity={0.5} /></RadarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="p-6">
                <div className="grid grid-cols-1 gap-3">
                  <div><Label className="mb-1.5 block">Strengths</Label><Textarea value={strengths} onChange={(e) => setStrengths(e.target.value)} rows={2} disabled={!canEval} data-testid="strengths" /></div>
                  <div><Label className="mb-1.5 block">Weaknesses</Label><Textarea value={weaknesses} onChange={(e) => setWeaknesses(e.target.value)} rows={2} disabled={!canEval} data-testid="weaknesses" /></div>
                </div>
              </Card>
            </div>
          </div>
          <Card className="p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold font-heading flex items-center gap-2"><Sparkles size={18} className="text-emerald-600" /> AI Development Report</h3>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={runAI} disabled={aiLoading} data-testid="run-ai-analysis">{aiLoading ? <Loader2 className="animate-spin" size={16} /> : <><Sparkles size={15} className="me-1" /> Generate</>}</Button>
            </div>
            {analysis ? <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm" data-testid="ai-analysis-result">{analysis}</div> : <p className="text-muted-foreground text-sm">Save scores and click Generate for an AI-powered development analysis. The AI provides recommendations for the coach to review — it does not make selection decisions.</p>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
